import { OptimizelyDecideOption, useDecision, } from '@optimizely/react-sdk';
import { IMMEDIATE_FEATURES, removeExperimentVariationFromCookie, setExperimentVariationInCookie, setUserExposedtoExperimentInCookie, } from 'utils/experiment';
import SegmentUtils from 'utils/segment';
export const OPTIMIZELY_FEATURE_ON = 'on';
export const OPTIMIZELY_FEATURE_OFF = 'off';
export const OPTIMIZELY_VARIATION_ORIGINAL = 'original';
export const OPTIMIZELY_VARIATION_REFRESHED = 'refreshed';
export const OPTIMIZELY_VARIATION_EXPRESS_CHECKOUT = 'express_checkout';
export const OPTIMIZELY_VARIATION_SWITCH_CTA_ORDER = 'switch_cta_order';
export function useFeatureVariationDecision(featureKey, { generateImpression } = {
    generateImpression: false,
}) {
    const decideOptions = [
        OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
    ];
    if (!generateImpression) {
        decideOptions.push(OptimizelyDecideOption.DISABLE_DECISION_EVENT);
    }
    const [decision] = useDecision(featureKey, {
        autoUpdate: true,
        decideOptions,
    });
    return decision;
}
export function useFeatureVariation(featureKey, { generateImpression } = {
    generateImpression: false,
}) {
    const decision = useFeatureVariationDecision(featureKey, {
        generateImpression,
    });
    return decision.enabled ? decision.variationKey : OPTIMIZELY_FEATURE_OFF;
}
/**
 * Returns the feature variation for the given feature key.
 * This function is for use in Class components or places where hooks are not usable.
 */
export const getFeatureVariation = (optimizely, featureKey, { generateImpression } = {
    generateImpression: false,
}) => {
    if (typeof window === 'undefined' || !optimizely) {
        return OPTIMIZELY_FEATURE_OFF;
    }
    const decideOptions = [
        OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
        OptimizelyDecideOption.INCLUDE_REASONS,
    ];
    if (!generateImpression) {
        decideOptions.push(OptimizelyDecideOption.DISABLE_DECISION_EVENT);
    }
    const decision = optimizely.decide(featureKey, decideOptions);
    if (featureKey === IMMEDIATE_FEATURES.EXPRESS_CHECKOUT_WEB_DESKTOP_REFRESH) {
        SegmentUtils.track({
            event: 'Test - Optimizely Decision For Bucketing',
            properties: decision,
            additionalOptions: {
                context: {
                    protocols: {
                        event_version: 1,
                    },
                },
            },
        });
    }
    if (decision.enabled) {
        setExperimentVariationInCookie(featureKey, decision.variationKey);
    }
    else {
        removeExperimentVariationFromCookie(featureKey);
    }
    if (decision.enabled) {
        return decision.variationKey;
    }
    return OPTIMIZELY_FEATURE_OFF;
};
export function useFeatureVariationWithVariables(featureKey, variableKey, shouldTrackExperimentInCookie = false) {
    const decideOptions = [
        OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
    ];
    const [decision] = useDecision(featureKey, {
        autoUpdate: true,
        decideOptions,
    });
    const isEnabled = decision?.enabled || false;
    const variableValue = decision?.variables?.[variableKey];
    const variationKey = decision?.variationKey;
    if (isEnabled) {
        setExperimentVariationInCookie(featureKey, decision.variationKey);
        if (shouldTrackExperimentInCookie) {
            setUserExposedtoExperimentInCookie(featureKey, 30); // Expire after 30 days
        }
    }
    else {
        removeExperimentVariationFromCookie(featureKey);
    }
    return {
        isEnabled,
        variableValue,
        variationKey,
    };
}
